import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { trackCta } from '@/lib/analytics';

export const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Subtle scroll fade
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(scrollY / (windowHeight * 0.6), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative bg-[#0a0a0a] overflow-hidden pt-[128px] md:pt-[200px] pb-[80px] md:pb-[160px]">
      {/* Restrained top radial vignette for subtle depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[560px]"
        style={{
          background:
            'radial-gradient(60% 100% at 50% 0%, rgba(0,229,255,0.05) 0%, rgba(10,10,10,0) 70%)',
        }}
      />

      <div
        className="container-professional text-center relative z-10"
        style={{
          opacity: 1 - scrollProgress * 0.5,
          transform: `translateY(${scrollProgress * 24}px)`,
        }}
      >
        {/* Headline */}
        <h1
          className="text-[40px] sm:text-5xl md:text-[56px] lg:text-[68px] font-bold text-white mb-6 leading-[1.05] tracking-[-0.028em] opacity-0 max-w-[20ch] mx-auto"
          style={{
            animation: isLoaded ? 'fade-up 0.6s ease-out forwards' : 'none',
            animationDelay: '0.1s',
          }}
        >
          We build apps your business will actually use.
        </h1>

        {/* Subheadline */}
        <p
          className="text-base md:text-lg text-white/70 max-w-xl mx-auto mb-9 leading-[1.55] opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '0.35s',
          }}
        >
          Custom web tools for sales teams, service businesses, and anyone who's outgrown spreadsheets and manual processes.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row justify-center items-center gap-3 mb-12 opacity-0"
          style={{
            animation: isLoaded ? 'fade-up 0.6s ease-out forwards' : 'none',
            animationDelay: '0.55s',
          }}
        >
          <Link to="/services" className="w-full sm:w-auto" onClick={trackCta('see_what_we_build', 'hero')}>
            <button
              className="group relative w-full sm:w-auto h-11 px-6 text-[14px] bg-primary hover:bg-[#1aeaff] text-[#001216] font-semibold rounded-md transition-colors duration-200 flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
            >
              <span>See what we build</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2.25} />
            </button>
          </Link>
          <Link to="/contact" className="w-full sm:w-auto" onClick={trackCta('book_free_call', 'hero')}>
            <button
              className="h-11 px-6 text-[14px] bg-transparent hover:bg-white/[0.04] text-white/85 hover:text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center border border-white/10 hover:border-white/20 w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
            >
              <span>Book a free call</span>
            </button>
          </Link>
        </div>

        {/* Body paragraph */}
        <p
          className="text-[14px] text-white/45 max-w-lg mx-auto leading-[1.6] opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '0.8s',
          }}
        >
          From sales prospecting tools to client portals, internal dashboards, and workflow automation, we build software that fits the way your business actually works. Fast delivery. Fixed price.
        </p>
      </div>
    </section>
  );
};
